import { fetchSeriesJson } from '@api/files';
import { downloadQueue } from 'utils/DownloadQueue';
import type {
  CprAngleFrame,
  CprCenterlinePathPoint,
  CprCrossSectionImages,
  CprFrameViewBox,
  CprMode,
  CprOverlayGeometry,
  CrossSectionFrameRef,
  CrossSectionImageAsset,
  ImageLayerLabelValues,
  ImageLayerKey,
  ImageTileRect,
} from './cprImageResources';
import {
  createServerCprBaseImagePath,
  makeServerLabelOverlayLayers,
} from './cprImageResources';

const CPR_IMAGE_MODES: readonly CprMode[] = ['straightened', 'stretched'];

type V2DisplayImage = {
  encoding?: string;
  normalization?: string;
  windowCenter?: number;
  windowWidth?: number;
  huMin?: number;
  huMax?: number;
  huSamplingSupported?: boolean;
};

type V2CprModeResource = {
  mode?: CprMode;
  manifest?: string;
  manifestPath?: string;
  frames?: V2CprFrame[];
  huPathTemplate?: string;
  labelPathTemplate?: string;
  mappingPathTemplate?: string;
};

type V2CrossSectionResource = {
  width?: number;
  height?: number;
  rawWidth?: number;
  rawHeight?: number;
  displayScale?: number;
  displayWidth?: number;
  displayHeight?: number;
  fieldOfViewMm?: number;
  spacingMm?: number;
  indexStart?: number;
  indexEnd?: number;
  frameCount?: number;
  indexMapPath?: string;
  assetManifestPath?: string;
  indexMap?: V2CrossSectionIndexMapEntry[];
  assetRefs?: V2CrossSectionIndexMapEntry[];
  assetRefCount?: number;
};

type V2BranchResource = {
  name?: string;
  source?: string;
  centerline?: string;
  centerlinePath?: string;
  manifestPath?: string;
  labelLayers?: V2LabelLayerMap;
  cpr?: Partial<Record<CprMode, V2CprModeResource>>;
  cprModes?: CprMode[];
  crossSection?: V2CrossSectionResource;
};

export type cprCrossSectionImages = {
  version: 2 | 3;
  revision: string;
  displayImage?: V2DisplayImage;
  labelLayers?: V2RootLabelLayers;
  branches: Record<string, V2BranchResource>;
};

type V2LabelLayerSpec =
  | number[]
  | {
      values?: number[];
    };

type V2LabelLayerMap = Partial<Record<Exclude<ImageLayerKey, 'base'>, V2LabelLayerSpec>>;

type V2RootLabelLayers =
  | V2LabelLayerMap
  | {
      coronary?: V2LabelLayerMap;
      aorta?: V2LabelLayerMap;
    };

type LooseV2LabelLayerMap = V2LabelLayerMap & {
  calcium?: V2LabelLayerSpec;
};

type V2CprMapping = {
  angle?: number;
  angleDeg?: number;
  mode?: CprMode;
  width?: number;
  height?: number;
  basePath?: string;
  huPath?: string;
  baseDtype?: string;
  baseEncoding?: string;
  baseCompression?: string;
  baseUncompressedBytes?: number;
  uncompressedBaseBytes?: number;
  compression?: string;
  huMin?: number;
  huMax?: number;
  labelPath?: string;
  labelCompression?: string;
  labelUncompressedBytes?: number;
  mappingPath?: string;
  localIndexStart?: number;
  localIndexEnd?: number;
  clientIndexStart?: number;
  clientIndexEnd?: number;
  sourceIndexStart?: number;
  sourceIndexEnd?: number;
  frontPaddingRows?: number;
  backPaddingRows?: number;
  validImageRowStart?: number;
  validImageRowEnd?: number;
  labelFlipY?: boolean;
  pathClientIndex?: number[];
  pathCenterlineIndex?: number[];
  rowCenterlineIndex?: number[];
  clientIndexByRow?: number[];
  rowByClientIndex?: number[];
  pathX?: number[];
  pathY?: number[];
  pathDistanceMm?: number[];
  rowDistanceMm?: number[];
  outputCenterX?: number;
  defaultViewBox?: CprFrameViewBox;
  overlayGeometry?: CprOverlayGeometry;
};

type V2CprFrame = V2CprMapping;

type V2CprFrameManifest = {
  version?: number;
  frames?: V2CprFrame[];
};

type V2CrossSectionAssetManifest = {
  version?: number;
  assetLayout?: string;
  assets?: Array<{
    assetId?: string;
    assetLayout?: string;
    branchId?: number | string;
    source?: string;
    sourceCenterlineIndex?: number;
    width?: number;
    height?: number;
    rawWidth?: number;
    rawHeight?: number;
    displayScale?: number;
    displayWidth?: number;
    displayHeight?: number;
    fieldOfViewMm?: number;
    spacingMm?: number;
    sourceSpacingMm?: number;
    samplingSpacingMm?: number;
    frameCount?: number;
    baseDtype?: string;
    labelDtype?: string;
    huMin?: number;
    huMax?: number;
    compression?: string;
    compressionLevel?: number;
    frameStrideBytes?: number;
    uncompressedBaseBytes?: number;
    uncompressedLabelBytes?: number;
    baseUncompressedBytes?: number;
    labelUncompressedBytes?: number;
    baseEncoding?: string;
    labelEncoding?: string;
    storageLayout?: string;
    displaySpacingMm?: number;
    baseRevision?: string;
    labelRevision?: string;
    zoomFactor?: number;
    labelSmoothing?: unknown;
    labelValueHistogram?: Record<string, number>;
    basePath?: string;
    labelPath?: string;
    mappingPath?: string;
  }>;
  pages?: Array<{
    pageId?: string;
    branchId?: number | string;
    pageIndex?: number;
    basePath?: string;
    labelPath?: string;
    rawWidth?: number;
    rawHeight?: number;
    displayScale?: number;
    displayWidth?: number;
    displayHeight?: number;
    pageWidth?: number;
    pageHeight?: number;
    tiles?: V2CrossSectionIndexMapEntry[];
  }>;
};

type V2CrossSectionIndexMapEntry = {
  index?: number;
  clientIndex?: number;
  localIndex?: number;
  assetId?: string;
  assetLayout?: string;
  pageIndex?: number;
  tileIndex?: number;
  tileRect?: ImageTileRect;
  sourceCenterlineIndex?: number;
  width?: number;
  height?: number;
  rawWidth?: number;
  rawHeight?: number;
  displayScale?: number;
  displayWidth?: number;
  displayHeight?: number;
  fieldOfViewMm?: number;
  spacingMm?: number;
  baseDtype?: string;
  labelDtype?: string;
  huMin?: number;
  huMax?: number;
  basePath?: string;
  labelPath?: string;
  mappingPath?: string;
  world?: number[];
  normal?: number[];
  tangent?: number[];
  binormal?: number[];
  resliceAxes?: number[][];
};

export type V2ManifestFetchJson = (target: string) => Promise<unknown>;

function enqueueManifestJsonFetch(
  task: () => Promise<unknown>,
  priority: number,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    downloadQueue.enqueue(async () => {
      try {
        resolve(await task());
      } catch (err) {
        reject(err);
      }
    }, priority);
  });
}

function isObject(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object';
}

export function iscprCrossSectionImages(
  value: unknown,
): value is cprCrossSectionImages {
  return (
    isObject(value) &&
    (value.version === 2 || value.version === 3) &&
    typeof value.revision === 'string' &&
    isObject(value.branches)
  );
}

function stripQuery(path: string) {
  return path.split('?')[0];
}

function stripLeading(path: string) {
  return path.replace(/^\.\//, '').replace(/^\/+/, '');
}

function decodeSeriesTarget(target: string) {
  return stripLeading(stripQuery(target).replace(/%2F/gi, '/'));
}

function encodeSeriesTarget(target: string) {
  return decodeSeriesTarget(target).replace(/\//g, '%2F');
}

function dirname(path: string) {
  const clean = stripLeading(stripQuery(path));
  const slash = clean.lastIndexOf('/');
  return slash >= 0 ? clean.slice(0, slash) : '';
}

function normalizeResourcePath(path: string) {
  const absolute = path.startsWith('/');
  const parts: string[] = [];
  stripLeading(path)
    .split('/')
    .filter((part) => part.length > 0 && part !== '.')
    .forEach((part) => {
      if (part === '..') {
        parts.pop();
        return;
      }
      parts.push(part);
    });
  return `${absolute ? '/' : ''}${parts.join('/')}`;
}

function resolveResourcePath({
  path,
  rootDir,
  baseDir,
}: {
  path: string | undefined;
  rootDir: string;
  baseDir?: string;
}) {
  if (!path) return undefined;
  if (
    path.startsWith('http://') ||
    path.startsWith('https://') ||
    path.startsWith('data:') ||
    path.startsWith('blob:') ||
    path.startsWith('mock://') ||
    path.startsWith('local-overlay://') ||
    path.startsWith('server-cpr-base://')
  ) {
    return path;
  }

  const clean = stripLeading(path);
  if (rootDir && clean.startsWith(`${rootDir}/`)) return clean;
  if (baseDir) return normalizeResourcePath(`${baseDir}/${clean}`);
  return normalizeResourcePath(rootDir ? `${rootDir}/${clean}` : clean);
}

function getRootDirFromManifestTarget(manifestTarget: string) {
  return dirname(decodeSeriesTarget(manifestTarget));
}

function getRootDirFromResourcePath(path: string) {
  const clean = decodeSeriesTarget(path);
  const branchMarker = '/branches/';
  const branchIndex = clean.indexOf(branchMarker);
  if (branchIndex > 0) return clean.slice(0, branchIndex);
  const assetMarker = '/crossSectionAssets/';
  const assetIndex = clean.indexOf(assetMarker);
  if (assetIndex > 0) return clean.slice(0, assetIndex);
  return dirname(clean);
}

function toNumber(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function toOptionalNumber(value: unknown) {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function selectHuRange(
  primary?: { huMin?: unknown; huMax?: unknown },
  fallback?: { huMin?: unknown; huMax?: unknown },
) {
  return {
    huMin: toOptionalNumber(primary?.huMin ?? fallback?.huMin),
    huMax: toOptionalNumber(primary?.huMax ?? fallback?.huMax),
  };
}

function toPositiveInt(value: unknown, fallback: number) {
  const n = Math.floor(toNumber(value, fallback));
  return n > 0 ? n : fallback;
}

function toInt(value: unknown, fallback = 0) {
  return Math.floor(toNumber(value, fallback));
}

function toOptionalVec3(value: unknown): [number, number, number] | undefined {
  if (!Array.isArray(value) || value.length < 3) return undefined;
  const tuple = value.slice(0, 3).map(Number);
  return tuple.every(Number.isFinite)
    ? ([tuple[0], tuple[1], tuple[2]] as [number, number, number])
    : undefined;
}

function toOptionalBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function toNumberArray(value: unknown): number[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const next = value.map(Number).filter(Number.isFinite);
  return next.length ? next : undefined;
}

function isAorticBranch(branch?: V2BranchResource) {
  const sourceText = `${branch?.source ?? ''} ${branch?.name ?? ''}`.toLowerCase();
  return sourceText.includes('aorta');
}

function normalizeLabelLayers(
  value: V2LabelLayerMap | undefined,
  options?: { aortic?: boolean },
): ImageLayerLabelValues | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const entries = Object.entries(value as LooseV2LabelLayerMap)
    .map(([key, spec]) => {
      const values = Array.isArray(spec)
        ? spec.map(Number).filter(Number.isFinite)
        : Array.isArray(spec?.values)
          ? spec.values.map(Number).filter(Number.isFinite)
          : [];
      return values.length ? [key, values] : null;
    })
    .filter((entry): entry is [string, number[]] => !!entry);
  if (!entries.length) return undefined;

  const normalized = Object.fromEntries(entries) as ImageLayerLabelValues & {
    calcium?: number[];
  };
  if (options?.aortic) {
    const aortaValues = normalized.aorta ?? normalized.lumen;
    const avcValues = normalized.avc ?? normalized.calcium;
    const aorticLayers: ImageLayerLabelValues = {};
    if (aortaValues?.length) {
      aorticLayers.aorta = aortaValues;
    }
    if (avcValues?.length) {
      aorticLayers.avc = avcValues;
    }
    return Object.keys(aorticLayers).length ? aorticLayers : undefined;
  }
  delete normalized.calcium;
  return normalized;
}

function selectRootLabelLayersForBranch({
  rootLabelLayers,
  branch,
}: {
  rootLabelLayers?: V2RootLabelLayers;
  branch?: V2BranchResource;
}): ImageLayerLabelValues | undefined {
  if (!rootLabelLayers || typeof rootLabelLayers !== 'object') return undefined;
  const aortic = isAorticBranch(branch);
  const direct = normalizeLabelLayers(rootLabelLayers as V2LabelLayerMap, {
    aortic,
  });
  if (direct) return direct;

  const grouped = rootLabelLayers as {
    coronary?: V2LabelLayerMap;
    aorta?: V2LabelLayerMap;
  };
  if (aortic) {
    return normalizeLabelLayers(grouped.aorta, { aortic: true });
  }
  return normalizeLabelLayers(grouped.coronary);
}

function selectBranchLabelLayers({
  branch,
  rootLabelLayers,
}: {
  branch?: V2BranchResource;
  rootLabelLayers?: V2RootLabelLayers;
}): ImageLayerLabelValues | undefined {
  const aortic = isAorticBranch(branch);
  return (
    normalizeLabelLayers(branch?.labelLayers, { aortic }) ??
    selectRootLabelLayersForBranch({ rootLabelLayers, branch })
  );
}

function toTileRect(value: unknown): ImageTileRect | undefined {
  if (!isObject(value)) return undefined;
  const x = Number(value.x);
  const y = Number(value.y);
  const width = Number(value.width);
  const height = Number(value.height);
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  return { x, y, width, height };
}

function toResliceAxes(value: unknown): number[][] | undefined {
  if (!Array.isArray(value) || value.length < 4) return undefined;
  const rows = value.slice(0, 4).map((row) =>
    Array.isArray(row) ? row.slice(0, 4).map(Number) : [],
  );
  return rows.every((row) => row.length === 4 && row.every(Number.isFinite))
    ? rows
    : undefined;
}

function normalizeViewBox(value: unknown): CprFrameViewBox | undefined {
  if (!isObject(value)) return undefined;
  const x = Number(value.x);
  const y = Number(value.y);
  const width = Number(value.width);
  const height = Number(value.height);
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return undefined;
  }
  return { x, y, width, height };
}

function buildCenterlinePathFromMapping(
  mapping: V2CprMapping,
  frameWidth: number,
  frameHeight: number,
): CprCenterlinePathPoint[] {
  const centerlineIndexes =
    Array.isArray(mapping.pathClientIndex) && mapping.pathClientIndex.length > 0
      ? mapping.pathClientIndex
      : Array.isArray(mapping.pathCenterlineIndex) &&
          mapping.pathCenterlineIndex.length > 0
        ? mapping.pathCenterlineIndex
      : Array.isArray(mapping.rowCenterlineIndex)
        ? mapping.rowCenterlineIndex
        : [];
  const xValues = Array.isArray(mapping.pathX) ? mapping.pathX : [];
  const yValues = Array.isArray(mapping.pathY) ? mapping.pathY : [];
  const distances = Array.isArray(mapping.pathDistanceMm)
    ? mapping.pathDistanceMm
    : [];
  const count = Math.max(centerlineIndexes.length, xValues.length, yValues.length);
  if (count <= 0) return [];

  const fallbackX = Number.isFinite(mapping.outputCenterX)
    ? Number(mapping.outputCenterX)
    : frameWidth * 0.5;
  const localStart = toOptionalNumber(mapping.localIndexStart);
  const sourceStart = toOptionalNumber(mapping.sourceIndexStart);

  return Array.from({ length: count }, (_value, offset) => {
    const index = toNumber(centerlineIndexes[offset], offset);
    const sourceCenterlineIndex =
      sourceStart !== undefined && localStart !== undefined
        ? sourceStart + (index - localStart)
        : undefined;
    const y =
      Number.isFinite(yValues[offset])
        ? Number(yValues[offset])
        : count === 1
          ? 0
          : (offset / (count - 1)) * Math.max(0, frameHeight - 1);
    const point: CprCenterlinePathPoint = {
      index,
      x: Number.isFinite(xValues[offset]) ? Number(xValues[offset]) : fallbackX,
      y,
    };
    if (Number.isFinite(distances[offset])) {
      point.distanceMm = Number(distances[offset]);
    }
    if (sourceCenterlineIndex !== undefined) {
      point.sourceCenterlineIndex = sourceCenterlineIndex;
    }
    return point;
  });
}

function hasInlineCprMapping(frame: V2CprFrame) {
  return (
    Array.isArray(frame.pathCenterlineIndex) ||
    Array.isArray(frame.rowCenterlineIndex) ||
    Array.isArray(frame.pathX) ||
    Array.isArray(frame.pathY) ||
    isObject(frame.overlayGeometry)
  );
}

function isRasterImagePath(path: string | undefined) {
  if (!path) return false;
  const clean = stripQuery(path).toLowerCase();
  return (
    clean.endsWith('.png') ||
    clean.endsWith('.jpg') ||
    clean.endsWith('.jpeg') ||
    clean.endsWith('.webp') ||
    clean.endsWith('.svg')
  );
}

function isCprBaseBinarySource(
  base: string,
  mapping: V2CprMapping,
) {
  if (base.startsWith('server-cpr-base://')) return false;
  if (stripQuery(base).toLowerCase().endsWith('.bin')) return true;
  if (isRasterImagePath(base)) return false;

  const text = `${mapping.baseEncoding ?? ''} ${mapping.baseDtype ?? ''}`.toLowerCase();
  return (
    text.includes('bin') ||
    text.includes('raw') ||
    text.includes('uint8') ||
    text.includes('int16')
  );
}

function normalizeCprBaseDtype(mapping: V2CprMapping) {
  const text = `${mapping.baseDtype ?? ''} ${mapping.baseEncoding ?? ''}`.toLowerCase();
  if (text.includes('int16')) return 'int16';
  if (text.includes('uint8')) return 'uint8';
  return 'uint8';
}

function resolveCprBaseLayerPath({
  base,
  mapping,
  width,
  height,
  displayImage,
}: {
  base: string;
  mapping: V2CprMapping;
  width: number;
  height: number;
  displayImage?: V2DisplayImage;
}) {
  if (!isCprBaseBinarySource(base, mapping)) return base;
  const huRange = selectHuRange(mapping, displayImage);
  return (
    createServerCprBaseImagePath(base, {
      outputWidth: width,
      outputHeight: height,
      compression:
        typeof mapping.baseCompression === 'string'
          ? mapping.baseCompression
          : typeof mapping.compression === 'string'
            ? mapping.compression
            : undefined,
      uncompressedBytes: toOptionalNumber(
        mapping.baseUncompressedBytes ?? mapping.uncompressedBaseBytes,
      ),
      dtype: normalizeCprBaseDtype(mapping),
      huMin: huRange.huMin,
      huMax: huRange.huMax,
    }) ?? base
  );
}

function normalizeCprFrame({
  frame,
  mapping,
  rootDir,
  modeBaseDir,
  angleDeg,
  labelLayers,
  displayImage,
}: {
  frame: V2CprFrame;
  mapping: V2CprMapping;
  rootDir: string;
  modeBaseDir?: string;
  angleDeg: number;
  labelLayers?: ImageLayerLabelValues;
  displayImage?: V2DisplayImage;
}): CprAngleFrame {
  const width = toPositiveInt(mapping.width ?? frame.width, 1);
  const height = toPositiveInt(mapping.height ?? frame.height, 1);
  const base = resolveResourcePath({
    path: mapping.huPath ?? mapping.basePath ?? frame.huPath ?? frame.basePath,
    rootDir,
    baseDir: modeBaseDir,
  });
  if (!base) {
    throw new Error(`CPR v2 base path missing: ${angleDeg}`);
  }
  const baseLayerPath = resolveCprBaseLayerPath({
    base,
    mapping,
    width,
    height,
    displayImage,
  });
  const labelPath = resolveResourcePath({
    path: mapping.labelPath ?? frame.labelPath,
    rootDir,
    baseDir: modeBaseDir,
  });

  return {
    angleDeg: toNumber(mapping.angleDeg ?? mapping.angle ?? angleDeg, angleDeg),
    width,
    height,
    labelPath,
    labelFlipY: toOptionalBoolean(mapping.labelFlipY),
    labelCompression:
      typeof mapping.labelCompression === 'string'
        ? mapping.labelCompression
        : undefined,
    labelUncompressedBytes: toOptionalNumber(mapping.labelUncompressedBytes),
    localIndexStart: toOptionalNumber(mapping.localIndexStart),
    localIndexEnd: toOptionalNumber(mapping.localIndexEnd),
    clientIndexStart: toOptionalNumber(mapping.clientIndexStart),
    clientIndexEnd: toOptionalNumber(mapping.clientIndexEnd),
    sourceIndexStart: toOptionalNumber(mapping.sourceIndexStart),
    sourceIndexEnd: toOptionalNumber(mapping.sourceIndexEnd),
    frontPaddingRows: toOptionalNumber(mapping.frontPaddingRows),
    backPaddingRows: toOptionalNumber(mapping.backPaddingRows),
    validImageRowStart: toOptionalNumber(mapping.validImageRowStart),
    validImageRowEnd: toOptionalNumber(mapping.validImageRowEnd),
    rowCenterlineIndex: toNumberArray(mapping.rowCenterlineIndex),
    clientIndexByRow: toNumberArray(mapping.clientIndexByRow),
    rowByClientIndex: toNumberArray(mapping.rowByClientIndex),
    pathClientIndex: toNumberArray(mapping.pathClientIndex),
    rowDistanceMm: toNumberArray(mapping.rowDistanceMm),
    pathDistanceMm: toNumberArray(mapping.pathDistanceMm),
    layers: {
      base: baseLayerPath,
      ...makeServerLabelOverlayLayers(labelPath, {
        flipY: mapping.labelFlipY === true,
        outputWidth: width,
        outputHeight: height,
        compression:
          typeof mapping.labelCompression === 'string'
            ? mapping.labelCompression
            : undefined,
        uncompressedBytes: toOptionalNumber(mapping.labelUncompressedBytes),
      }, labelLayers),
    },
    centerlinePath: buildCenterlinePathFromMapping(mapping, width, height),
    defaultViewBox: normalizeViewBox(mapping.defaultViewBox),
    overlayGeometry: mapping.overlayGeometry,
  };
}

async function loadCprModeFrames({
  branchId,
  mode,
  rootDir,
  modeResource,
  fetchJson,
  labelLayers,
  displayImage,
}: {
  branchId: string;
  mode: CprMode;
  rootDir: string;
  modeResource: V2CprModeResource;
  fetchJson: V2ManifestFetchJson;
  labelLayers?: ImageLayerLabelValues;
  displayImage?: V2DisplayImage;
}) {
  const modeManifestPath = resolveResourcePath({
    path: modeResource.manifest ?? modeResource.manifestPath,
    rootDir,
  });
  if (!modeManifestPath) {
    throw new Error(`CPR v2 mode manifest missing: ${branchId}/${mode}`);
  }

  const modeBaseDir = dirname(modeManifestPath);
  const modeManifest = (await fetchJson(modeManifestPath)) as V2CprFrameManifest;
  const frames = Array.isArray(modeManifest.frames) ? modeManifest.frames : [];

  const angleFrames = await Promise.all(
    frames.map(async (frame) => {
      const angleDeg = toNumber(frame.angleDeg ?? frame.angle, 0);
      const mappingPath = resolveResourcePath({
        path: frame.mappingPath,
        rootDir,
        baseDir: modeBaseDir,
      });
      const mapping = hasInlineCprMapping(frame)
        ? (frame as V2CprMapping)
        : mappingPath
        ? ((await fetchJson(mappingPath)) as V2CprMapping)
        : ({} as V2CprMapping);
      return normalizeCprFrame({
        frame,
        mapping: {
          ...frame,
          ...mapping,
        },
        rootDir,
        modeBaseDir,
        angleDeg,
        labelLayers,
        displayImage,
      });
    }),
  );

  return {
    mode,
    angleFrames,
  };
}

function toAssetRecord({
  rootDir,
  assetManifest,
  displayImage,
}: {
  rootDir: string;
  assetManifest: V2CrossSectionAssetManifest;
  displayImage?: V2DisplayImage;
}) {
  const assets: Record<string, CrossSectionImageAsset> = {};
  (assetManifest.assets ?? []).forEach((asset) => {
    if (!asset.assetId || !asset.basePath) return;
    const basePath = resolveResourcePath({
      path: asset.basePath,
      rootDir,
    });
    if (!basePath) return;
    const width = toPositiveInt(
      (asset as V2CrossSectionIndexMapEntry).displayWidth ?? asset.width,
      1,
    );
    const height = toPositiveInt(
      (asset as V2CrossSectionIndexMapEntry).displayHeight ?? asset.height,
      1,
    );
    const huRange = selectHuRange(asset, displayImage);
    assets[asset.assetId] = {
      assetId: asset.assetId,
      assetLayout: asset.assetLayout ?? assetManifest.assetLayout,
      branchId: asset.branchId,
      source: asset.source,
      width,
      height,
      rawWidth: toOptionalNumber((asset as V2CrossSectionIndexMapEntry).rawWidth),
      rawHeight: toOptionalNumber((asset as V2CrossSectionIndexMapEntry).rawHeight),
      displayScale: toOptionalNumber(
        (asset as V2CrossSectionIndexMapEntry).displayScale,
      ),
      displayWidth: toOptionalNumber(
        (asset as V2CrossSectionIndexMapEntry).displayWidth,
      ),
      displayHeight: toOptionalNumber(
        (asset as V2CrossSectionIndexMapEntry).displayHeight,
      ),
      fieldOfViewMm: toOptionalNumber(asset.fieldOfViewMm),
      spacingMm: toOptionalNumber(asset.spacingMm),
      sourceSpacingMm: toOptionalNumber(asset.sourceSpacingMm),
      samplingSpacingMm: toOptionalNumber(asset.samplingSpacingMm),
      frameCount: toOptionalNumber(asset.frameCount),
      baseDtype: asset.baseDtype,
      labelDtype: asset.labelDtype,
      huMin: huRange.huMin,
      huMax: huRange.huMax,
      compression: asset.compression,
      compressionLevel: toOptionalNumber(asset.compressionLevel),
      frameStrideBytes: toOptionalNumber(asset.frameStrideBytes),
      uncompressedBaseBytes: toOptionalNumber(
        asset.uncompressedBaseBytes ?? asset.baseUncompressedBytes,
      ),
      uncompressedLabelBytes: toOptionalNumber(
        asset.uncompressedLabelBytes ?? asset.labelUncompressedBytes,
      ),
      labelSmoothing: asset.labelSmoothing,
      labelValueHistogram: asset.labelValueHistogram,
      sourceCenterlineIndex: toOptionalNumber(asset.sourceCenterlineIndex),
      basePath,
      labelPath: resolveResourcePath({
        path: asset.labelPath,
        rootDir,
      }),
      mappingPath: resolveResourcePath({
        path: asset.mappingPath,
        rootDir,
      }),
      world: toOptionalVec3((asset as V2CrossSectionIndexMapEntry).world),
      normal: toOptionalVec3((asset as V2CrossSectionIndexMapEntry).normal),
      tangent: toOptionalVec3((asset as V2CrossSectionIndexMapEntry).tangent),
      binormal: toOptionalVec3((asset as V2CrossSectionIndexMapEntry).binormal),
      resliceAxes: toResliceAxes(
        (asset as V2CrossSectionIndexMapEntry).resliceAxes,
      ),
    };
  });
  (assetManifest.pages ?? []).forEach((page) => {
    const pageBasePath = resolveResourcePath({
      path: page.basePath,
      rootDir,
    });
    if (!pageBasePath) return;
    const pageLabelPath = resolveResourcePath({
      path: page.labelPath,
      rootDir,
    });
    (page.tiles ?? []).forEach((tile) => {
      if (!tile.assetId) return;
      assets[tile.assetId] = {
        assetId: tile.assetId,
        clientIndex: toOptionalNumber(tile.clientIndex),
        localIndex: toOptionalNumber(tile.localIndex ?? tile.index),
        pageIndex: toOptionalNumber(tile.pageIndex ?? page.pageIndex),
        tileIndex: toOptionalNumber(tile.tileIndex),
        tileRect: toTileRect(tile.tileRect),
        width: toPositiveInt(tile.displayWidth ?? page.displayWidth, 1),
        height: toPositiveInt(tile.displayHeight ?? page.displayHeight, 1),
        rawWidth: toOptionalNumber(tile.rawWidth ?? page.rawWidth),
        rawHeight: toOptionalNumber(tile.rawHeight ?? page.rawHeight),
        displayScale: toOptionalNumber(tile.displayScale ?? page.displayScale),
        displayWidth: toOptionalNumber(tile.displayWidth ?? page.displayWidth),
        displayHeight: toOptionalNumber(tile.displayHeight ?? page.displayHeight),
        fieldOfViewMm: toOptionalNumber(tile.fieldOfViewMm),
        spacingMm: toOptionalNumber(tile.spacingMm),
        sourceCenterlineIndex: toOptionalNumber(tile.sourceCenterlineIndex),
        basePath: pageBasePath,
        labelPath: pageLabelPath,
        world: toOptionalVec3(tile.world),
        normal: toOptionalVec3(tile.normal),
        tangent: toOptionalVec3(tile.tangent),
        binormal: toOptionalVec3(tile.binormal),
        resliceAxes: toResliceAxes(tile.resliceAxes),
      };
    });
  });
  return assets;
}

function toAssetRecordFromIndexMap({
  rootDir,
  entries,
  displayImage,
}: {
  rootDir: string;
  entries: V2CrossSectionIndexMapEntry[];
  displayImage?: V2DisplayImage;
}) {
  const assets: Record<string, CrossSectionImageAsset> = {};
  entries.forEach((entry) => {
    if (!entry.assetId || !entry.basePath) return;
    const basePath = resolveResourcePath({
      path: entry.basePath,
      rootDir,
    });
    if (!basePath) return;
    const width = toPositiveInt(
      entry.displayWidth ?? entry.width ?? entry.rawWidth,
      1,
    );
    const height = toPositiveInt(
      entry.displayHeight ?? entry.height ?? entry.rawHeight,
      1,
    );
    const huRange = selectHuRange(entry, displayImage);
    assets[entry.assetId] = {
      assetId: entry.assetId,
      assetLayout: entry.assetLayout,
      clientIndex: toOptionalNumber(entry.clientIndex),
      localIndex: toOptionalNumber(entry.localIndex ?? entry.index),
      pageIndex: toOptionalNumber(entry.pageIndex),
      tileIndex: toOptionalNumber(entry.tileIndex),
      tileRect: toTileRect(entry.tileRect),
      width,
      height,
      rawWidth: toOptionalNumber(entry.rawWidth),
      rawHeight: toOptionalNumber(entry.rawHeight),
      displayScale: toOptionalNumber(entry.displayScale),
      displayWidth: toOptionalNumber(entry.displayWidth),
      displayHeight: toOptionalNumber(entry.displayHeight),
      fieldOfViewMm: toOptionalNumber(entry.fieldOfViewMm),
      spacingMm: toOptionalNumber(entry.spacingMm),
      baseDtype: entry.baseDtype,
      labelDtype: entry.labelDtype,
      huMin: huRange.huMin,
      huMax: huRange.huMax,
      sourceCenterlineIndex: toOptionalNumber(entry.sourceCenterlineIndex),
      basePath,
      labelPath: resolveResourcePath({
        path: entry.labelPath,
        rootDir,
      }),
      mappingPath: resolveResourcePath({
        path: entry.mappingPath,
        rootDir,
      }),
      world: toOptionalVec3(entry.world),
      normal: toOptionalVec3(entry.normal),
      tangent: toOptionalVec3(entry.tangent),
      binormal: toOptionalVec3(entry.binormal),
      resliceAxes: toResliceAxes(entry.resliceAxes),
    };
  });
  return assets;
}

function toAssetByIndex(indexMap: unknown) {
  const entries = Array.isArray(indexMap)
    ? (indexMap as V2CrossSectionIndexMapEntry[])
    : [];
  const assetByIndex: Record<number, string> = {};
  entries.forEach((entry) => {
    const localIndex = Number(entry?.clientIndex ?? entry?.localIndex ?? entry?.index);
    if (!Number.isFinite(localIndex) || !entry?.assetId) return;
    assetByIndex[localIndex] = entry.assetId;
  });
  return assetByIndex;
}

function toFrameByIndex(indexMap: unknown) {
  const entries = Array.isArray(indexMap)
    ? (indexMap as V2CrossSectionIndexMapEntry[])
    : [];
  const frameByIndex: Record<number, CrossSectionFrameRef> = {};
  entries.forEach((entry) => {
    const clientIndex = Number(entry?.clientIndex ?? entry?.localIndex ?? entry?.index);
    if (!Number.isFinite(clientIndex) || !entry?.assetId) return;
    frameByIndex[clientIndex] = {
      assetId: entry.assetId,
      clientIndex,
      localIndex: toOptionalNumber(entry.localIndex ?? entry.index),
      sourceCenterlineIndex: toOptionalNumber(entry.sourceCenterlineIndex),
      world: toOptionalVec3(entry.world),
      normal: toOptionalVec3(entry.normal),
      tangent: toOptionalVec3(entry.tangent),
      binormal: toOptionalVec3(entry.binormal),
      resliceAxes: toResliceAxes(entry.resliceAxes),
    };
  });
  return frameByIndex;
}

function firstReferencedAsset(
  assets: Record<string, CrossSectionImageAsset>,
  assetByIndex: Record<number, string>,
) {
  const firstId = Object.keys(assetByIndex)
    .sort((a, b) => Number(a) - Number(b))
    .map((index) => assetByIndex[Number(index)])
    .find((assetId) => !!assets[assetId]);
  if (firstId) return assets[firstId];
  return Object.values(assets)[0];
}

type LoadedV2BranchManifest = V2BranchResource & {
  version?: number;
  branchId?: number | string;
};

function normalizeCprModeFromBranchManifest({
  rootDir,
  mode,
  modeResource,
  labelLayers,
  displayImage,
}: {
  rootDir: string;
  mode: CprMode;
  modeResource: V2CprModeResource;
  labelLayers?: ImageLayerLabelValues;
  displayImage?: V2DisplayImage;
}) {
  const frames = Array.isArray(modeResource.frames) ? modeResource.frames : [];
  return {
    mode,
    angleFrames: frames.map((frame) => {
      const angleDeg = toNumber(frame.angleDeg ?? frame.angle, 0);
      return normalizeCprFrame({
        frame,
        mapping: frame,
        rootDir,
        angleDeg,
        labelLayers,
        displayImage,
      });
    }),
  };
}

function getCrossSectionIndexEntries(
  crossSection: V2CrossSectionResource | undefined,
): V2CrossSectionIndexMapEntry[] {
  if (Array.isArray(crossSection?.indexMap)) return crossSection.indexMap;
  if (Array.isArray(crossSection?.assetRefs)) return crossSection.assetRefs;
  return [];
}

function normalizeBranchManifest({
  rootDir,
  branchId,
  branchManifest,
  fallbackBranch,
  crossSectionAssets,
  displayImage,
}: {
  rootDir: string;
  branchId: string;
  branchManifest: LoadedV2BranchManifest;
  fallbackBranch?: V2BranchResource;
  crossSectionAssets?: Record<string, CrossSectionImageAsset>;
  displayImage?: V2DisplayImage;
}): CprCrossSectionImages['branches'][string] {
  const labelLayers = selectBranchLabelLayers({
    branch: {
      ...fallbackBranch,
      ...branchManifest,
      labelLayers: branchManifest.labelLayers ?? fallbackBranch?.labelLayers,
    },
  });
  const cprEntries = CPR_IMAGE_MODES
    .map((mode) => {
      const modeResource = branchManifest.cpr?.[mode];
      if (!modeResource?.frames?.length) return null;
      return [
        mode,
        normalizeCprModeFromBranchManifest({
          rootDir,
          mode,
          modeResource,
          labelLayers,
          displayImage,
        }),
      ] as const;
    })
    .filter(
      (
        entry,
      ): entry is readonly [
        CprMode,
        ReturnType<typeof normalizeCprModeFromBranchManifest>,
      ] => !!entry,
    );

  const crossSection = branchManifest.crossSection ?? fallbackBranch?.crossSection;
  const entries = getCrossSectionIndexEntries(crossSection);
  const assetsFromIndexMap = toAssetRecordFromIndexMap({
    rootDir,
    entries,
    displayImage,
  });
  const assets = {
    ...(crossSectionAssets ?? {}),
    ...assetsFromIndexMap,
  };
  const assetByIndex = toAssetByIndex(entries);
  const frameByIndex = toFrameByIndex(entries);
  const primaryAsset = firstReferencedAsset(assets, assetByIndex);
  const indexValues = entries
    .map((entry) => Number(entry.clientIndex ?? entry.localIndex ?? entry.index))
    .filter(Number.isFinite);
  const indexStart =
    Number.isFinite(crossSection?.indexStart)
      ? toInt(crossSection?.indexStart, 0)
      : indexValues.length
        ? Math.min(...indexValues)
        : 0;
  const indexEnd =
    Number.isFinite(crossSection?.indexEnd)
      ? toInt(crossSection?.indexEnd, indexStart)
      : indexValues.length
        ? Math.max(...indexValues)
        : indexStart;

  return {
    name: branchManifest.name ?? fallbackBranch?.name,
    source: branchManifest.source ?? fallbackBranch?.source,
    manifestPath: fallbackBranch?.manifestPath,
    loaded: true,
    labelLayers,
    cpr: Object.fromEntries(cprEntries),
    crossSection: {
      width: toPositiveInt(
        crossSection?.displayWidth ??
          crossSection?.width ??
          primaryAsset?.displayWidth ??
          primaryAsset?.width,
        1,
      ),
      height: toPositiveInt(
        crossSection?.displayHeight ??
          crossSection?.height ??
          primaryAsset?.displayHeight ??
          primaryAsset?.height,
        1,
      ),
      rawWidth: toOptionalNumber(crossSection?.rawWidth ?? primaryAsset?.rawWidth),
      rawHeight: toOptionalNumber(crossSection?.rawHeight ?? primaryAsset?.rawHeight),
      displayScale: toOptionalNumber(
        crossSection?.displayScale ?? primaryAsset?.displayScale,
      ),
      displayWidth: toOptionalNumber(
        crossSection?.displayWidth ?? primaryAsset?.displayWidth,
      ),
      displayHeight: toOptionalNumber(
        crossSection?.displayHeight ?? primaryAsset?.displayHeight,
      ),
      fieldOfViewMm: toOptionalNumber(
        crossSection?.fieldOfViewMm ?? primaryAsset?.fieldOfViewMm,
      ),
      spacingMm: toOptionalNumber(crossSection?.spacingMm ?? primaryAsset?.spacingMm),
      frameCount: toOptionalNumber(crossSection?.frameCount ?? primaryAsset?.frameCount),
      indexStart,
      indexEnd,
      indexMapPath: resolveResourcePath({
        path: crossSection?.indexMapPath,
        rootDir,
      }),
      assetManifestPath: resolveResourcePath({
        path: crossSection?.assetManifestPath,
        rootDir,
      }),
      labelLayers,
      assetByIndex,
      frameByIndex,
      assets,
    },
  };
}

export async function loadcprCrossSectionImages({
  accessToken,
  seriesId,
  manifestTarget,
  rootManifest,
  CacheControl,
  fetchJson,
}: {
  accessToken: string;
  seriesId: string;
  manifestTarget: string;
  rootManifest: cprCrossSectionImages;
  CacheControl?: Record<string, string>;
  fetchJson?: V2ManifestFetchJson;
}): Promise<CprCrossSectionImages> {
  const rootDir = getRootDirFromManifestTarget(manifestTarget);
  const loadJson: V2ManifestFetchJson =
    fetchJson ??
    ((target) =>
      enqueueManifestJsonFetch(
        () =>
          fetchSeriesJson({
            accessToken,
            seriesId,
            target: encodeSeriesTarget(target),
            CacheControl,
          }) as Promise<unknown>,
        100,
      ));

  const hasDeferredBranchManifests = Object.values(rootManifest.branches).some(
    (branch) => typeof branch.manifestPath === 'string' && branch.manifestPath.length > 0,
  );

  if (hasDeferredBranchManifests) {
    const branches = Object.fromEntries(
      Object.entries(rootManifest.branches).map(([branchId, branch]) => {
        const crossSection = branch.crossSection;
        const indexStart = toInt(crossSection?.indexStart, 0);
        const labelLayers = selectBranchLabelLayers({
          branch,
          rootLabelLayers: rootManifest.labelLayers,
        });
        return [
          branchId,
          {
            name: branch.name,
            source: branch.source,
            manifestPath: resolveResourcePath({
              path: branch.manifestPath,
              rootDir,
            }),
            loaded: false,
            labelLayers,
            cpr: {},
            crossSection: {
              width: toPositiveInt(
                crossSection?.displayWidth ?? crossSection?.width,
                1,
              ),
              height: toPositiveInt(
                crossSection?.displayHeight ?? crossSection?.height,
                1,
              ),
              rawWidth: toOptionalNumber(crossSection?.rawWidth),
              rawHeight: toOptionalNumber(crossSection?.rawHeight),
              displayScale: toOptionalNumber(crossSection?.displayScale),
              displayWidth: toOptionalNumber(crossSection?.displayWidth),
              displayHeight: toOptionalNumber(crossSection?.displayHeight),
              fieldOfViewMm: toOptionalNumber(crossSection?.fieldOfViewMm),
              spacingMm: toOptionalNumber(crossSection?.spacingMm),
              frameCount: toOptionalNumber(crossSection?.frameCount),
              indexStart,
              indexEnd: toInt(crossSection?.indexEnd, indexStart),
              indexMapPath: resolveResourcePath({
                path: crossSection?.indexMapPath,
                rootDir,
              }),
              assetManifestPath: resolveResourcePath({
                path: crossSection?.assetManifestPath,
                rootDir,
              }),
              labelLayers,
              assetByIndex: {},
              frameByIndex: {},
              assets: {},
            },
          },
        ];
      }),
    ) as CprCrossSectionImages['branches'];

    return {
      revision: rootManifest.revision,
      displayImage: rootManifest.displayImage,
      branches,
    };
  }

  const assetManifestPath = Object.values(rootManifest.branches)
    .map((branch) => branch.crossSection?.assetManifestPath)
    .find((path): path is string => typeof path === 'string' && path.length > 0);
  const resolvedAssetManifestPath = resolveResourcePath({
    path: assetManifestPath,
    rootDir,
  });
  const crossSectionAssetManifest = resolvedAssetManifestPath
    ? ((await loadJson(resolvedAssetManifestPath)) as V2CrossSectionAssetManifest)
    : ({ assets: [] } as V2CrossSectionAssetManifest);
  const crossSectionAssets = toAssetRecord({
    rootDir,
    assetManifest: crossSectionAssetManifest,
    displayImage: rootManifest.displayImage,
  });

  const branches = Object.fromEntries(
    await Promise.all(
      Object.entries(rootManifest.branches).map(async ([branchId, branch]) => {
        const labelLayers = selectBranchLabelLayers({
          branch,
          rootLabelLayers: rootManifest.labelLayers,
        });
        const cprEntries = await Promise.all(
          CPR_IMAGE_MODES.map(async (mode) => {
            const modeResource = branch.cpr?.[mode];
            if (!modeResource) return null;
            return [
              mode,
              await loadCprModeFrames({
                branchId,
                mode,
                rootDir,
                modeResource,
                fetchJson: loadJson,
                labelLayers,
                displayImage: rootManifest.displayImage,
              }),
            ] as const;
          }),
        );

        const crossSection = branch.crossSection;
        const indexMapPath = resolveResourcePath({
          path: crossSection?.indexMapPath,
          rootDir,
        });
        const indexMap = indexMapPath ? await loadJson(indexMapPath) : [];
        const assetByIndex = toAssetByIndex(indexMap);
        const frameByIndex = toFrameByIndex(indexMap);

        return [
          branchId,
          {
            cpr: Object.fromEntries(
              cprEntries.filter(
                (
                  entry,
                ): entry is readonly [CprMode, Awaited<ReturnType<typeof loadCprModeFrames>>] =>
                  !!entry,
              ),
            ),
            name: branch.name,
            source: branch.source,
            manifestPath: resolveResourcePath({
              path: branch.manifestPath,
              rootDir,
            }),
            loaded: true,
            labelLayers,
            crossSection: {
              width: toPositiveInt(
                crossSection?.displayWidth ?? crossSection?.width,
                1,
              ),
              height: toPositiveInt(
                crossSection?.displayHeight ?? crossSection?.height,
                1,
              ),
              rawWidth: toOptionalNumber(crossSection?.rawWidth),
              rawHeight: toOptionalNumber(crossSection?.rawHeight),
              displayScale: toOptionalNumber(crossSection?.displayScale),
              displayWidth: toOptionalNumber(crossSection?.displayWidth),
              displayHeight: toOptionalNumber(crossSection?.displayHeight),
              fieldOfViewMm: Number.isFinite(crossSection?.fieldOfViewMm)
                ? Number(crossSection?.fieldOfViewMm)
                : undefined,
              spacingMm: Number.isFinite(crossSection?.spacingMm)
                ? Number(crossSection?.spacingMm)
                : undefined,
              frameCount: Number.isFinite(crossSection?.frameCount)
                ? Number(crossSection?.frameCount)
                : undefined,
              indexStart: Math.floor(toNumber(crossSection?.indexStart, 0)),
              indexEnd: Math.floor(toNumber(crossSection?.indexEnd, 0)),
              indexMapPath,
              assetManifestPath: resolvedAssetManifestPath,
              labelLayers,
              assetByIndex,
              frameByIndex,
              assets: crossSectionAssets,
            },
          },
        ];
      }),
    ),
  ) as CprCrossSectionImages['branches'];

  return {
    revision: rootManifest.revision,
    displayImage: rootManifest.displayImage,
    branches,
  };
}

const pendingBranchLoads = new Map<
  string,
  Promise<{
    branchId: string;
    branch: CprCrossSectionImages['branches'][string];
  }>
>();

export function iscprCrossSectionImagesBranchDeferred(
  manifest: CprCrossSectionImages | null | undefined,
  branchId: number | string | null | undefined,
) {
  if (!manifest || branchId === null || branchId === undefined) return false;
  const branch = manifest.branches[String(branchId)];
  return !!branch?.manifestPath && branch.loaded !== true;
}

export async function loadcprCrossSectionImagesBranchResource({
  accessToken,
  seriesId,
  manifest,
  branchId,
  CacheControl,
  fetchJson,
}: {
  accessToken: string;
  seriesId: string;
  manifest: CprCrossSectionImages;
  branchId: number | string;
  CacheControl?: Record<string, string>;
  fetchJson?: V2ManifestFetchJson;
}): Promise<{
  branchId: string;
  branch: CprCrossSectionImages['branches'][string];
}> {
  const branchKey = String(branchId);
  const fallbackBranch = manifest.branches[branchKey] as
    | (V2BranchResource & CprCrossSectionImages['branches'][string])
    | undefined;
  const manifestPath = fallbackBranch?.manifestPath;
  if (!manifestPath) {
    throw new Error(`CPR v2 branch manifest missing: ${branchKey}`);
  }
  if (fallbackBranch.loaded) {
    return {
      branchId: branchKey,
      branch: fallbackBranch,
    };
  }

  const cacheKey = `${seriesId}|${manifest.revision}|${branchKey}|${manifestPath}`;
  const existing = pendingBranchLoads.get(cacheKey);
  if (existing) return existing;

  const loadJson: V2ManifestFetchJson =
    fetchJson ??
    ((target) =>
      enqueueManifestJsonFetch(
        () =>
          fetchSeriesJson({
            accessToken,
            seriesId,
            target: encodeSeriesTarget(target),
            CacheControl,
          }) as Promise<unknown>,
        80,
      ));

  const promise = (async () => {
    const rootDir = getRootDirFromResourcePath(manifestPath);
    const rawBranchManifest = (await loadJson(
      manifestPath,
    )) as LoadedV2BranchManifest;
    let branchManifest = rawBranchManifest;
    if (
      !getCrossSectionIndexEntries(branchManifest.crossSection).length &&
      branchManifest.crossSection?.indexMapPath
    ) {
      const indexMapPath = resolveResourcePath({
        path: branchManifest.crossSection.indexMapPath,
        rootDir,
      });
      const indexMap = indexMapPath ? await loadJson(indexMapPath) : [];
      if (Array.isArray(indexMap)) {
        branchManifest = {
          ...branchManifest,
          crossSection: {
            ...branchManifest.crossSection,
            assetRefs: indexMap as V2CrossSectionIndexMapEntry[],
          },
        };
      }
    }
    const crossSection = branchManifest.crossSection ?? fallbackBranch?.crossSection;
    const assetManifestPath = resolveResourcePath({
      path: crossSection?.assetManifestPath,
      rootDir,
    });
    const crossSectionAssetManifest = assetManifestPath
      ? ((await loadJson(assetManifestPath)) as V2CrossSectionAssetManifest)
      : ({ assets: [] } as V2CrossSectionAssetManifest);
    const crossSectionAssets = toAssetRecord({
      rootDir,
      assetManifest: crossSectionAssetManifest,
      displayImage: manifest.displayImage,
    });
    return {
      branchId: branchKey,
      branch: normalizeBranchManifest({
        rootDir,
        branchId: branchKey,
        branchManifest,
        fallbackBranch,
        crossSectionAssets,
        displayImage: manifest.displayImage,
      }),
    };
  })();

  pendingBranchLoads.set(cacheKey, promise);
  try {
    return await promise;
  } finally {
    pendingBranchLoads.delete(cacheKey);
  }
}

export function mergecprCrossSectionImagesBranchResource(
  manifest: CprCrossSectionImages,
  branchId: number | string,
  branch: CprCrossSectionImages['branches'][string],
): CprCrossSectionImages {
  const branchKey = String(branchId);
  return {
    ...manifest,
    branches: {
      ...manifest.branches,
      [branchKey]: {
        ...manifest.branches[branchKey],
        ...branch,
      },
    },
  };
}

export const __cprCrossSectionImagesTestUtils = {
  decodeSeriesTarget,
  encodeSeriesTarget,
  getRootDirFromManifestTarget,
  resolveResourcePath,
  toAssetByIndex,
  getRootDirFromResourcePath,
};
